/**
 * Header-aware row helpers.
 *
 * Sheets API hands us values as `string[]` arrays paired with header rows.
 * `noUncheckedIndexedAccess` is on, so we always coerce missing cells to
 * empty strings to keep call sites tidy.
 */

import type { TabName } from './schema';
import { TABS, columnLetter } from './schema';

export function rowToObject(headers: readonly string[], row: readonly string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < headers.length; i++) {
    const h = headers[i];
    if (h == null) continue;
    out[h] = row[i] ?? '';
  }
  return out;
}

export function objectToRow(
  headers: readonly string[],
  obj: Record<string, string>,
): string[] {
  return headers.map((h) => (obj[h] != null ? String(obj[h]) : ''));
}

export function headersForTab(tab: TabName): readonly string[] {
  return TABS[tab].headers;
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function tabRowRange(tab: TabName, rowNumber: number): string {
  const headers = TABS[tab].headers;
  const lastCol = columnLetter(headers.length - 1);
  return `${tab}!A${rowNumber}:${lastCol}${rowNumber}`;
}

/**
 * Case-insensitive truthy-set check.
 *
 * Why a set instead of `=== 'true'`: a user editing the Sheet by hand who
 * writes `TRUE` / `1` / `yes` for `is_default` previously got `false`
 * silently. Edge case: only book has `is_default=TRUE` → default-book
 * fallback fails → blank app. Be liberal in what we accept (LIMITED to
 * the explicit set below — never a JS truthiness coerce).
 */
const TRUTHY_SET: ReadonlySet<string> = new Set([
  'true',
  '1',
  'yes',
  'y',
]);

export function parseBool(v: string | undefined): boolean {
  if (v == null) return false;
  return TRUTHY_SET.has(v.trim().toLowerCase());
}

/**
 * Thrown when `parseInt10` receives a non-empty but unparseable value.
 *
 * Distinguishing missing (use default) from unparseable (throw) is the
 * Section 6 fix: silent fallback for garbage was masking corrupt cells
 * as freshly-created rows. Callers MUST decide explicitly whether to
 * skip the row, log + ignore, or propagate.
 */
export class InvalidIntError extends Error {
  readonly value: string;
  constructor(value: string) {
    super(`Cannot parse as integer: ${JSON.stringify(value)}`);
    this.name = 'InvalidIntError';
    this.value = value;
  }
}

/**
 * Parse an integer cell.
 *
 * - Empty / whitespace / undefined → return `fallback`.
 * - Non-numeric → throw `InvalidIntError` (callers should wrap in
 *   try/catch + skip-row or log + ignore as appropriate).
 * - Otherwise → return the parsed integer.
 */
export function parseInt10(v: string | undefined, fallback = 0): number {
  if (v == null) return fallback;
  const trimmed = v.trim();
  if (trimmed === '') return fallback;
  const n = Number.parseInt(trimmed, 10);
  // `Number.parseInt` is permissive (`'1.5x'` → 1). Use a strict regex
  // to reject mixed alphanumeric + force callers to fix upstream.
  if (!Number.isFinite(n) || !/^-?\d+$/.test(trimmed)) {
    throw new InvalidIntError(v);
  }
  return n;
}
